import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject } from '@angular/core';

import { ChdTableAlign, ChdTablePresenter, ChdTableRenderColumn } from '@cp-produkte/cruise-hospitality-design';

import { ComponentsModule } from '../components.module';
import { Column, PaginatedResponse, Pagination, Row, wrap } from '../spa-table/spa-table.component';

const ALIGN_MAP: Record<ChdTableAlign, 'left' | 'center' | 'right'> = {
  start: 'left',
  center: 'center',
  end: 'right',
};

/**
 * App-local renderer that draws `ChdTable` with the SpaManager `spa-table`
 * (legacy `dx-table`). Plugged into the chd table slot via the backend provider,
 * e.g. `provideChdDxBackend({ table: ChdTableSpaRenderer })`.
 *
 * Feature gaps vs. the Material renderer: already-translated column labels are
 * fed through `spa-table`'s Transloco pipe (logged as missing keys); sorting has
 * no `spa-table` equivalent. The paginator follows the presenter's `showPaginator`:
 * paginated rows drive `spa-table`'s remote pager off the current page, while
 * unpaginated rows are passed as a plain array sized to fit on one page so no
 * pager is shown.
 */
@Component({
  selector: 'spa-chd-table-renderer',
  imports: [ComponentsModule, NgTemplateOutlet],
  templateUrl: './chd-table-spa-renderer.component.html',
  host: {
    // dx-table dispatches native, bubbling events whose names collide with ChdTable's
    // outputs; stop them here so the host chd-table doesn't re-handle them as its own.
    '(pageChanged)': '$event.stopPropagation()',
    '(sortChanged)': '$event.stopPropagation()',
    '(rowClicked)': '$event.stopPropagation()',
  },
})
export class ChdTableSpaRenderer {
  protected readonly presenter = inject(ChdTablePresenter);

  protected readonly columns = computed<Column<unknown>[]>(() => {
    const vs = this.presenter.viewState();
    const columns = vs.renderColumns.map((renderColumn) => this.toColumn(renderColumn));
    if (vs.actionsTemplate) {
      columns.push({ key: vs.actionsColumnKey, hideHeader: true, settings: { align: 'right' } });
    }
    return columns;
  });

  protected readonly rows = computed<PaginatedResponse<Row<unknown>> | Row<unknown>[]>(() => {
    const vs = this.presenter.viewState();
    const elements = vs.rows.map((row) => wrap(row, vs.rowUnread(row)));

    if (!vs.showPaginator) {
      return elements;
    }

    const size = vs.pageSize || vs.totalLength || 1;
    return {
      page: {
        size,
        totalElements: vs.totalLength,
        totalPages: Math.max(1, Math.ceil(vs.totalLength / size)),
        number: vs.pageIndex,
      },
      elements,
    };
  });

  // Unpaginated tables fit all rows on one page so `spa-table`'s managed pager stays hidden.
  protected readonly dxPageSize = computed<number>(() => {
    const vs = this.presenter.viewState();
    return vs.showPaginator ? vs.pageSize : Math.max(vs.rows.length, 1);
  });

  protected readonly pageSizeOptions = computed<number[]>(() => [...this.presenter.viewState().pageSizeOptions]);

  protected readonly selectedRow = computed<Row<unknown> | undefined>(() => {
    const selected = this.presenter.viewState().selectedRow;
    return selected === null ? undefined : wrap(selected);
  });

  protected readonly compareRows = computed(() => {
    const compareWith = this.presenter.viewState().compareWith;
    return (first: Row<unknown>, second: Row<unknown>): boolean =>
      first.element !== undefined && second.element !== undefined && compareWith(first.element, second.element);
  });

  protected onRowClick(row: Row<unknown>): void {
    if (row?.element !== undefined) {
      this.presenter.onRowClick(row.element);
    }
  }

  protected onPaginate(event: Pagination): void {
    this.presenter.onPageChange(event.page, event.pageSize);
  }

  private toColumn(renderColumn: ChdTableRenderColumn<unknown>): Column<unknown> {
    const column = renderColumn.column;
    const value = column.value;
    return {
      key: column.key,
      translationKey: column.label,
      hideHeader: column.label === undefined,
      value: value ? (row: Row<unknown>) => value(row.element) : undefined,
      settings: {
        align: column.align ? ALIGN_MAP[column.align] : undefined,
        sizeStyle: {
          minWidth: renderColumn.minWidth ?? undefined,
          maxWidth: renderColumn.maxWidth ?? undefined,
        },
      },
    };
  }
}
